import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Edit, Shield, Users as UsersIcon } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import { useGetCustomersQuery } from "../customersApi";
import { useCanCreateCustomers } from "../useCustomerPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// "Basic View" pane of the master-detail split, second module on this
// pattern after Employee Master (2026-08-29) - built on the same reusable
// MasterDetailListShell/MasterDetailRow, so this file only supplies
// Customer-specific data fetching, filters, and row content. Same
// Edit/Guarding/Bulk Entry actions as the old CustomersList table, just as
// row icons instead of table-cell buttons.
const CustomerCompactList = () => {
  const canCreate = useCanCreateCustomers();
  const navigate = useNavigate();
  const { branchId: selectedBranchIdParam, custId: selectedCustId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: customers = [], isLoading } = useGetCustomersQuery({
    search: search || undefined,
    isActive: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });

  const initials = (c) => (c.custName || "?").trim().slice(0, 2).toUpperCase();

  return (
    <MasterDetailListShell
      title="Customers"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.customerNew)}>
            <PlusCircle className="me-2" size={16} />
            Add New Customer
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search customer name"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select
              className="form-control form-control-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No customers match these filters."
      items={customers}
      getKey={(c) => `${c.branchId}-${c.custId}`}
      resetKey={`${search}|${status}|${selectedBranchId}`}
      renderItem={(c) => {
        const isSelected =
          String(c.branchId) === String(selectedBranchIdParam) && String(c.custId) === String(selectedCustId);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.customerEdit(c.branchId, c.custId))}
            avatarLabel={initials(c)}
            title={
              <>
                {c.custName || "-"}
                {c.isMapped && (
                  <span className="badge bg-info-transparent ms-2" title={`Home branch: ${c.branchCode}`}>
                    Shared from {c.branchCode}
                  </span>
                )}
              </>
            }
            subtitle={c.branchCode}
            badge={
              <span className={`badge ${c.isActive ? "badge-linesuccess" : "badge-linedanger"} me-2`}>
                {c.isActive ? "Active" : "Inactive"}
              </span>
            }
            actions={
              <>
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.customerEdit(c.branchId, c.custId));
                  }}
                >
                  <Edit size={15} />
                </button>
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="Guarding Assignments"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.customerGuarding(c.branchId, c.custId));
                  }}
                >
                  <Shield size={15} />
                </button>
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="Bulk Entry"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.customerBulkGuards(c.branchId, c.custId));
                  }}
                >
                  <UsersIcon size={15} />
                </button>
              </>
            }
          />
        );
      }}
    />
  );
};

export default CustomerCompactList;
